import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const AppointmentContext = createContext();

export const useAppointment = () => {
  const context = useContext(AppointmentContext);
  if (!context) {
    throw new Error('useAppointment must be used within an AppointmentProvider');
  }
  return context;
};

export const AppointmentProvider = ({ children }) => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);

  // Load appointments from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('bridgecare_appointments');
    if (stored) {
      try {
        setAppointments(JSON.parse(stored));
      } catch (error) {
        console.error('Failed to parse appointments:', error);
      }
    } else {
      // Initialize with mock data for demonstration
      const mockAppointments = [
        {
          id: '1',
          patientId: user?.id || 'demo',
          date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
          time: '10:00 AM',
          doctor: 'Dr. Amina Yusuf',
          specialty: 'Obstetrics',
          type: 'Antenatal Check-up',
          status: 'pending',
          mode: 'video',
          serviceId: 'antenatal',
          amount: 5000,
          createdAt: new Date().toISOString(),
        },
        {
          id: '2',
          patientId: user?.id || 'demo',
          date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
          time: '2:30 PM',
          doctor: 'Dr. Chidi Okafor',
          specialty: 'Pediatrics',
          type: 'Child Immunization Consultation',
          status: 'pending',
          mode: 'audio',
          serviceId: 'immunization',
          amount: 3000,
          createdAt: new Date().toISOString(),
        },
      ];
      setAppointments(mockAppointments);
      localStorage.setItem('bridgecare_appointments', JSON.stringify(mockAppointments));
    }
  }, [user]);

  // Save to localStorage whenever appointments change
  useEffect(() => {
    if (appointments.length > 0) {
      localStorage.setItem('bridgecare_appointments', JSON.stringify(appointments));
    }
  }, [appointments]);

  const createAppointment = (appointmentData) => {
    const newAppointment = {
      id: `APT${Date.now()}`,
      patientId: user?.id,
      status: 'pending_doctor_confirmation', // Requires doctor acknowledgment
      createdAt: new Date().toISOString(),
      ...appointmentData,
    };
    setAppointments(prev => [...prev, newAppointment]);
    return newAppointment;
  };

  const doctorConfirmAppointment = (appointmentId) => {
    setAppointments(prev =>
      prev.map(apt =>
        apt.id === appointmentId
          ? { ...apt, status: 'confirmed', doctorConfirmedAt: new Date().toISOString() }
          : apt
      )
    );
  };

  const confirmAppointment = (appointmentId) => {
    setAppointments(prev =>
      prev.map(apt =>
        apt.id === appointmentId
          ? { ...apt, status: 'confirmed', confirmedAt: new Date().toISOString() }
          : apt
      )
    );
  };

  const cancelAppointment = (appointmentId, reason) => {
    setAppointments(prev =>
      prev.map(apt =>
        apt.id === appointmentId
          ? {
              ...apt,
              status: 'cancelled',
              cancelledAt: new Date().toISOString(),
              cancelReason: reason,
              cancelledBy: user?.id,
            }
          : apt
      )
    );
  };

  const rescheduleAppointment = (appointmentId, newDate, newTime) => {
    setAppointments(prev =>
      prev.map(apt =>
        apt.id === appointmentId
          ? {
              ...apt,
              date: newDate,
              time: newTime,
              status: 'pending',
              rescheduledAt: new Date().toISOString(),
            }
          : apt
      )
    );
  };

  const completeAppointment = (appointmentId, notes) => {
    setAppointments(prev =>
      prev.map(apt =>
        apt.id === appointmentId
          ? {
              ...apt,
              status: 'completed',
              completedAt: new Date().toISOString(),
              notes: notes,
            }
          : apt
      )
    );
  };

  const getPatientAppointments = (patientId) => {
    return appointments.filter(apt => apt.patientId === patientId);
  };

  const getUpcomingAppointments = (patientId) => {
    return appointments.filter(
      apt =>
        apt.patientId === patientId &&
        ['pending', 'confirmed', 'pending_doctor_confirmation'].includes(apt.status) &&
        new Date(apt.date) >= new Date()
    );
  };

  const getPastAppointments = (patientId) => {
    return appointments.filter(
      apt =>
        apt.patientId === patientId &&
        (['completed', 'cancelled'].includes(apt.status) ||
          new Date(apt.date) < new Date())
    );
  };

  const value = {
    appointments,
    createAppointment,
    bookAppointment: createAppointment,
    confirmAppointment,
    doctorConfirmAppointment,
    cancelAppointment,
    rescheduleAppointment,
    completeAppointment,
    getPatientAppointments,
    getUpcomingAppointments,
    getPastAppointments,
  };

  return <AppointmentContext.Provider value={value}>{children}</AppointmentContext.Provider>;
};
